import React, { useEffect, useState, useMemo } from 'react';
import {
  FiHome,
  FiAward,
  FiClock,
  FiCalendar,
  FiUser,
  FiBell,
  FiLogOut,
  FiCheckCircle,
  FiWatch,
  FiClipboard,
  FiMessageCircle,
  FiChevronDown,
  FiChevronRight,
  FiArrowLeft,
} from 'react-icons/fi';
import Logo from "../assets/img/logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { getUnreadCount } from '../../store/Notification/userNotificationsSlice';
import { fetchMyModules } from '../../store/EmployeeModules/employeeModulesSlice';

const SidebarComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const empId = localStorage.getItem("_id");
  const notificationCount = useSelector((state) => state.notifications.unreadCount);
  const { allowedModules, loaded } = useSelector((state) => state.employeeModules);

  useEffect(() => {
    dispatch(getUnreadCount(empId));
    dispatch(fetchMyModules());
  }, [dispatch, empId, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("_id");
    navigate("/login");
  };

  const getActiveItem = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return 'dashboard';
    if (path.includes('timeTracker')) return 'timetracker';
    if (path.includes('calendar')) return 'calendar';
    if (path.includes('profile')) return 'profile';
    if (path.includes('notifications')) return 'notifications';
    if (path.includes('shiftTime')) return 'shiftTime';
    if (path.includes('leaveManagement')) return 'leaveManagement';
    if (path.includes('time-tracker-request')) return 'time-tracker-request';
    if (path.includes('documents')) return 'documents';
    if (path.includes('rewards')) return 'rewards';
    if (path.includes('chat')) return 'chat';
    return '';
  };

  const activeItem = getActiveItem();

  const menuCategories = [
    {
      title: "Time & Attendance",
      cat: 1,
      icon: <FiClock size={20} />,
      items: [
        { title: "Dashboard", icon: <FiHome size={20} />, path: "/dashboard", key: "dashboard", active: 0 },
        { title: "Time Tracker", icon: <FiClock size={20} />, path: "/timeTracker", key: "timetracker", active: 1 },
        { title: "Tracker Status", icon: <FiCheckCircle size={20} />, path: "/time-tracker-request", key: "time-tracker-request", active: 2 },
        { title: "Chat", icon: <FiMessageCircle size={20} />, path: "/chat", key: "chat", active: 2 },
      ],
    },
    {
      title: "Leave Management",
      cat: 2,
      icon: <FiClipboard size={20} />,
      items: [
        { title: "Manage Leaves", icon: <FiClipboard size={20} />, path: "/leaveManagement", key: "leaveManagement", active: 3 },
        { title: "Rewards", icon: <FiAward size={20} />, path: "/rewards", key: "rewards", active: 4 },
      ],
    },
    {
      title: "Account",
      cat: 3,
      icon: <FiUser size={20} />,
      items: [
        { title: "Profile", icon: <FiUser size={20} />, path: "/profile", key: "profile", active: 5 },
      ],
    },
    {
      title: "Reports",
      cat: 4,
      icon: <FiWatch size={20} />,
      items: [
        { title: "OT Calculations", icon: <FiWatch size={20} />, path: "/shiftTime", key: "shiftTime", active: 6 },
        { title: "Calendar", icon: <FiCalendar size={20} />, path: "/calendar", key: "calendar", active: 7 },
      ],
    },
    {
      title: "Notifications",
      cat: 5,
      icon: <FiBell size={20} />,
      items: [
        { title: "Notifications", icon: <FiBell size={20} />, path: "/notifications", key: "notifications", active: 8, badge: true },
      ],
    },
  ];

  const normalizePath = (path) => path.replace(/\/$/, "");
  const currentPath = normalizePath(location.pathname);

  const currentCategory = menuCategories.find((category) =>
    category.items.some(
      (item) =>
        normalizePath(item.path) === currentPath ||
        currentPath.startsWith(`${normalizePath(item.path)}/`)
    )
  );

  // Allowed paths from vendor-assigned employee modules (dashboard is always allowed)
  const allowedPaths = useMemo(() => {
    if (!loaded || !allowedModules?.length) return null;
    const paths = new Set(
      allowedModules.flatMap((m) => (m.routes || []).map((r) => normalizePath(r.path)))
    );
    paths.add('/dashboard');
    paths.add('/dashboard-new');
    return paths;
  }, [loaded, allowedModules]);

  const filteredMenuCategories = useMemo(() => {
    if (!allowedPaths) return menuCategories;
    return menuCategories
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => allowedPaths.has(normalizePath(item.path))),
      }))
      .filter((category) => category.items.length > 0);
  }, [allowedPaths]);

  const [openCategory, setOpenCategory] = useState(currentCategory ? currentCategory.cat : null);

  const toggleCategory = (cat) => {
    setOpenCategory(openCategory === cat ? null : cat);
  };

  return (
    <div className="bg-white shadow-2xl text-gray-700 w-64 min-h-screen p-2">
      {/* Logo */}
      <div className="p-6 text-center">
        <img
          onClick={() => navigate("/")}
          src={Logo}
          alt="Logo"
          className="h-12 cursor-pointer mx-auto"
        />
      </div>

      {/* Menu Items */}
      <div className="flex-1 px-1 py-1 overflow-y-auto">
        <div
          className={`flex items-center p-3 mb-1 rounded-lg cursor-pointer transition-colors ${
            activeItem === 'chat' ? "bg-green-100 text-green-700 font-medium" : "hover:bg-gray-100"
          }`}
          onClick={() => navigate('/dashboard-new')}
        >
          <span className={`mr-3 ${activeItem === 'chat' ? "text-green-600" : "text-gray-500"}`}>
            <FiArrowLeft size={20} />
          </span>
          <span className="flex-1">Main Menu</span>
        </div>

        {filteredMenuCategories.map((category) => {
          if (currentCategory && category.cat !== currentCategory.cat) return null;

          const isOpen = openCategory === category.cat;
          return (
            <div key={category.cat} className="mb-2">
              <div
                className="flex items-center p-3 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() => toggleCategory(category.cat)}
              >
                <span className="mr-3 text-gray-500">{category.icon}</span>
                <span className="flex-1 font-medium">{category.title}</span>
                <span className="text-gray-500">
                  {isOpen ? <FiChevronDown size={20} /> : <FiChevronRight size={20} />}
                </span>
              </div>
              {isOpen && (
                <div className="pl-6">
                  {category.items.map((item) => (
                    <div
                      key={item.key}
                      className={`flex items-center p-3 mb-1 rounded-lg cursor-pointer transition-colors ${
                        activeItem === item.key
                          ? "bg-green-100 text-green-700 font-medium"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => navigate(item.path)}
                    >
                      <span
                        className={`mr-3 ${
                          activeItem === item.key ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span className="flex-1">{item.title}</span>
                      {item.badge && notificationCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-semibold rounded-full px-2 py-1">
                          {notificationCount}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <div
          className="flex items-center p-3 rounded-lg cursor-pointer hover:bg-gray-100"
          onClick={handleLogout}
        >
          <span className="mr-3 text-gray-500">
            <FiLogOut size={20} />
          </span>
          <span className="flex-1">Logout</span>
        </div>
      </div>
    </div>
  );
};

export default SidebarComponent;
